import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, WhatsappLogo, ShieldCheck } from "@phosphor-icons/react";
import { SITE, IMAGES } from "@/constants/site";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} id="top" className="relative min-h-[100svh] flex items-center overflow-hidden bg-linen pt-[72px]" data-testid="hero-section">
      {/* curtain reveal */}
      <motion.div
        className="fixed inset-0 z-[80] bg-forest pointer-events-none"
        initial={{ y: 0 }}
        animate={{ y: "-100%" }}
        transition={{ duration: 0.9, delay: 0.15, ease: [0.76, 0, 0.24, 1] }}
      />

      {/* ambient shapes */}
      <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-mint-pale blur-3xl opacity-70" />
      <div className="absolute bottom-0 right-0 w-[560px] h-[560px] rounded-full bg-sage blur-3xl opacity-90" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 grid lg:grid-cols-12 gap-16 items-center w-full">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <MaskedLine delay={0.5}>
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-forest bg-mint-pale px-4 py-2 rounded-full">
              <ShieldCheck size={14} weight="fill" /> Insurance · Loans · Peace of Mind
            </span>
          </MaskedLine>

          <h1 className="mt-8 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tighter text-ink text-balance">
            <MaskedLine delay={0.65}>Protect everything</MaskedLine>
            <MaskedLine delay={0.8}>
              <span className="italic text-forest">you've worked for.</span>
            </MaskedLine>
          </h1>

          <MaskedLine delay={1.0} className="mt-8 max-w-xl">
            <p className="text-base sm:text-lg text-moss leading-relaxed">
              Honest, independent advice on medical, life, motor and pension cover — plus home
              and commercial property loans through leading banks and NBFCs. One advisor,
              every step of the way.
            </p>
          </MaskedLine>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <motion.a href="#contact" data-testid="hero-cta-quote" whileTap={{ scale: 0.97 }}
              className="bg-forest text-white font-semibold px-8 py-4 rounded-full hover:bg-forest-soft transition-colors duration-300 flex items-center gap-2">
              Get a Free Quote <ArrowDown size={16} weight="bold" />
            </motion.a>
            <motion.a href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Hi, I'd like to discuss insurance/loan options.")}`}
              target="_blank" rel="noopener noreferrer" data-testid="hero-cta-whatsapp" whileTap={{ scale: 0.97 }}
              className="border border-forest/25 text-forest font-semibold px-8 py-4 rounded-full hover:bg-mint-pale transition-colors duration-300 flex items-center gap-2">
              <WhatsappLogo size={18} weight="fill" /> WhatsApp Us
            </motion.a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="mt-10 text-xs tracking-wide text-moss"
          >
            Associated with leading insurers, banks &amp; NBFCs across India
          </motion.p>
        </motion.div>

        <div className="lg:col-span-5 relative hidden lg:block">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.2, delay: 0.9, ease: [0.76, 0, 0.24, 1] }}
            className="relative rounded-[2rem] overflow-hidden shadow-2xl shadow-forest/20"
          >
            <motion.img
              src={IMAGES.hero}
              alt="Family protected together at sunset"
              style={{ y: imgY, scale: 1.15 }}
              className="w-full h-[560px] object-cover"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-forest/40 via-transparent to-transparent" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -bottom-8 -left-12 bg-white/80 backdrop-blur-xl border border-black/5 rounded-2xl p-5 shadow-xl shadow-forest/10"
            data-testid="hero-trust-card"
          >
            <p className="font-serif text-3xl text-forest">15+ yrs</p>
            <p className="text-xs text-moss mt-1 tracking-wide">of trusted advisory &amp; claim support</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
